import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  MenuItem,
  Grid,
  Autocomplete,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Formik, Form, Field } from "formik";
import Swal from "sweetalert2";
import { backend } from "../../api";
import * as Yup from "yup";
import { makeStyles } from "@material-ui/core";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ClipLoader } from "react-spinners";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useMyContext } from "../../contextApi/context";
import { Country, State, City } from "country-state-city";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(4),
    width: "100%",
    color: "#fff",
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(2),
    marginRight: "20px",
    [theme.breakpoints.down("sm")]: {
      marginRight: "0px",
      padding: theme.spacing(2),
      width: "85%",
    },
  },
  button: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
    color: "#fff",
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1),
    },
  },
  signInButton: {
    color: theme.palette.primary.main,
  },
  stepNumber: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
  },
  accordionTitle: {
    color: "#fff",
  },
  accordionSummary: {
    display: "flex",
    alignItems: "center",
  },
  accordion: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(2),
    },
  },
  successIcon: {
    color: theme.palette.success.main,
    marginLeft: theme.spacing(1),
  },
  cardElement: {
    backgroundColor: "#333",
    borderRadius: "4px",
    padding: "25px 15px",
    marginTop: "12px",
    transition: "box-shadow 0.15s ease",
    "&:hover": {
      border: "1px solid #fff",
    },
  },
  darkTextField: {
    marginTop: "20px",
    "& .MuiOutlinedInput-root": {
      color: "#fff", // Text color
      backgroundColor: "#333", // Background color
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#fff", // Hover border color
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main, // Focused border color
      },
    },
    "& .MuiFormLabel-root": {
      color: "#fff", // Label color
    },
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1),
    },
  },
  btn: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.light,
    minWidth: "110px",
    margin: "10px 0",
    float: "right",
    fontSize: "16px",
    height: "100%",
    fontWeight: "600",
    borderRadius: "2px",
    padding: "10px 10px",
    "&:hover": {
      backgroundColor: theme.palette.secondary.light,
      color: theme.palette.primary.main,
    },
    [theme.breakpoints.down("sm")]: {
      float: "none",
      width: "100%",
      margin: theme.spacing(1, 0),
    },
  },
  fullWidthOnSmall: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  centerOnSmall: {
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
    },
  },
  emptyCart: {
    textAlign: "center",
    marginTop: theme.spacing(4),
    color: "#fff",
  },
}));

const PaymentForm = () => {
  const classes = useStyles();
  const { currentUser } = useSelector((state) => state.auth);
  const [step, setStep] = useState(0);
  const [bookItems, setBookItems] = useState([]);
  const [articleItems, setArticleItems] = useState([]);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const { cartItems, setCartItems } = useMyContext();
  const [selectedPayment, setSelectedPayment] = useState("bank_account");
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();

  const initialValues = {
    email: currentUser?.user?.email || "",
    name: "",
    contact_email: "",
    phone_number: "",
    address: "",
    country: "",
    country_code: "",
    state: "",
    state_name: "",
    city: "",
    postcode: "",
    street1: "",
    cover_source_url: "",
    interior_source_url: "",
    quantity: "",
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Required"),
  });

  const getCountries = () => {
    return Country.getAllCountries().map((country) => ({
      label: country.name,
      value: country.name,
      countryCode: country.isoCode,
    }));
  };

  const getStates = (country) => {
    return State.getStatesOfCountry(country).map((state) => ({
      label: state.name,
      value: state.name,
      stateCode: state.isoCode,
    }));
  };
  const getCities = (country, state) => {
    return City.getCitiesOfState(country, state).map((city) => ({
      label: city.name,
      value: city.name,
      cityCode: city.isoCode,
    }));
  };

  const isOnlyEbook = bookItems.every((item) => item.bookOrderType === "EBOOK");

  const adjustedStep = isOnlyEbook ? Math.min(step, 1) : step;

  //   console.log("Country: ", getCountries());
  //   console.log("State: ", getStates("US"));
  //   console.log("City: ", getCities("US", "NY"));

  const handleCheckout = async (data, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    const errors = [];
    const token = localStorage.getItem("Token");
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      let paymentMethod = null;

      if (selectedPayment === "bank_account") {
        const { paymentMethod: stripePaymentMethod, error } =
          await stripe.createPaymentMethod({
            type: "card",
            card: elements.getElement(CardElement),
            billing_details: { email: data?.email },
          });

        if (error) {
          toast.error(error.message);
          return;
        }

        paymentMethod = stripePaymentMethod;
      }

      // Create book and article orders, collecting their IDs
      const bookOrderIds = [];
      const articleOrderIds = [];
      const articlePromises = articleItems.map(async (item) => {
        try {
          const articlePrice =
            (item.articleId?.number_of_pages || 0) *
            (item.articleId?.per_page_price || 0);
          const taxAmount = (articlePrice * taxPercentage) / 100;
          const totalPrice = articlePrice + taxAmount;

          const articleOrderData = {
            total_price: totalPrice,
            status: "placed",
            source_of_approaching: "web",
            article: item.articleId?._id || "",
            billing_details: {
              payment_type: "paypal",
              number_of_pages: item.articleId?.number_of_pages || 0,
              per_page_price: item.articleId?.per_page_price || 0,
              tax_amount: [{ amount: taxAmount }],
            },
            payment_result: { email: data?.email },
          };

          const response = await backend.post(
            "/articleorder",
            articleOrderData,
            config
          );

          const createdArticleOrderId = response?.data?.data?._id;
          if (createdArticleOrderId)
            articleOrderIds.push(createdArticleOrderId);
          return createdArticleOrderId;
        } catch (err) {
          console.log(err)
          if (err.response && err.response.data) {
            const serverErrorMessage = err.response.data.message || "Unknown server error";
            errors.push(`${serverErrorMessage}`);
          }
        }
      });

      const bookPromises = bookItems.map(async (item) => {
        try {
          const deliveryAmount =
            item.bookOrderType === "BOTH" || item.bookOrderType === "HARDCOPY"
              ? 5.9 + (item.quantity - 1) * 2
              : 0;
          const bookPrice =
            item.bookOrderType === "BOTH"
              ? item.bookId?.price?.both_price || 0
              : item.bookOrderType === "HARDCOPY"
              ? (item.bookId?.price?.hardcover_price || 0) * item.quantity
              : item.bookId?.price?.digital_price || 0;
          const taxAmount = bookPrice * (taxPercentage / 100);
          const totalPrice = bookPrice + taxAmount;

          const bookOrderData = {
            total_price: totalPrice,
            book_price: { digital_price: bookPrice },
            status: item.bookId?.pre_order
              ? "pre order"
              : item.bookOrderType === "EBOOK"
              ? "delivered"
              : "placed",
            source_of_approaching: "web",
            book: item.bookId?._id || "",
            billing_details: {
              payment_type: "paypal",
              delivery_type: "author",
              delivery_amount: deliveryAmount,
              tax_amount: [{ amount: taxAmount }],
            },
            payment_result: { email: data?.email },
            bookOrderType: item.bookOrderType,
          };

          let luluPayload = {};
          if (
            item.bookOrderType === "BOTH" ||
            item.bookOrderType === "HARDCOPY"
          ) {
            luluPayload = {
              country: data.country,
              shipping_address: {
                ...data,
                name: data.street1,
                zip: data.postcode,
                state_code: data.state,
                cover_source_url: item.bookId?.ebook_file || "",
                interior_source_url: item.bookId?.cover_files?.paper_back || "",
                quantity: item.quantity || 1,
              },
            };

            if (!item.bookId.pre_order) {
              const luluResponse = await backend.post(
                "/lulu/shipping",
                luluPayload.shipping_address,
                config
              );
              console.log("Lulu API response:", luluResponse?.data);
              const errors =
                luluResponse?.data?.shipping_address?.detail?.errors;
              if (errors?.length > 0) {
                const message = errors?.[0].message || "Api failed";
                throw new Error(`Lulu shipping failed: ${message}`);
              }
              bookOrderData.LuluOrderId = luluResponse?.data?.id;
            }
          }

          const response = await backend.post(
            "/bookorder",
            { ...bookOrderData, ...luluPayload },
            config
          );

          const createdBookOrderId = response?.data?.data?._id;
          if (createdBookOrderId) bookOrderIds.push(createdBookOrderId); // Collect the order ID
        } catch (err) {
          console.log(err)
          if (err.response && err.response.data) {
            const serverErrorMessage = err.response.data.message || "Unknown server error";
            errors.push(`${serverErrorMessage}`);
          }
        }
      });

      // Wait for all orders to complete
      await Promise.all([...articlePromises, ...bookPromises]);

      if (errors.length > 0) {
        const formattedErrors = `
          <div>
            <strong>Errors:</strong>
            ${errors.map((err, index) => `<div>${index + 1}. ${err}</div>`).join("")}
          </div>
        `;
      
        toast.error(<div dangerouslySetInnerHTML={{ __html: formattedErrors }} />, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      
        return; // Stop further processing
      }

      const totalCost = parseFloat(localStorage.getItem("totalCost"));

      const payment = {
        amount: Number(totalCost) || 0,
        payment_method: paymentMethod ? paymentMethod.id : null,
        payment_type: selectedPayment,
        email: data.email,
        bookOrderIds,
        articleOrderIds,
        customer: currentUser?.user?._id,
        ...(selectedPayment === "e_money" && { eMoneyAccount: data.emoneyAccount }),
      };

      const paymentResponse = await backend.post(
        "/checkout/create-payment",
        payment,
        config
      );

      if (!paymentResponse.data.success) {
        toast.error("Payment failed");
        return;
      }

      // Clear the shopping cart
      await backend.delete("/shopCart", config);

      setCartItems([]);

      // Display success message and redirect the user
      const swalOptions = {
        icon: "success",
        title: "Order Placed!",
        html: `Your order has been placed successfully and the cart has been cleared.`,
        confirmButtonText: "View Order",
        showCancelButton: true,
        cancelButtonText: "Shop More",
        preConfirm: () => {
          history.push(`/dashboard/${localStorage.getItem("Role")}`, {
            activeTab: 1,
          });
        },
      };

      Swal.fire(swalOptions);

      resetForm();
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(`Checkout failed: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const generatePaylod = async () => {
    const token = localStorage.getItem("Token");
    let articles = [];
    let books = [];

    try {
      const response = await backend.get("/shopCart", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log("response: ", response);
      const orders = response?.data?.data || [];
      orders.forEach((order) => {
        if (order?.cartItemType === "BOOK") {
          books.push(order);
        }
        if (order?.cartItemType === "ARTICLE") {
          articles.push(order);
        }
      });

      setBookItems(books);
      setArticleItems(articles);
    } catch (error) {
      console.error("error: ", error);
    }
  };

  const fetchTaxPercentage = async () => {
    try {
      const response = await backend.get("/amount");
      const taxPercentage = response.data.data[0].tax;
      setTaxPercentage(taxPercentage);
    } catch (error) {
      console.error("Error fetching tax percentage:", error);
    }
  };

  useEffect(() => {
    generatePaylod();
    fetchTaxPercentage();
  }, []);

  return (
    <>
      <Paper
        className={classes.paper}
        style={{ backgroundColor: "#000", border: "1px solid #FECE00" }}
      >
        {bookItems.length === 0 && articleItems.length === 0 ? (
          <Typography variant="h5" className={classes.emptyCart}>
            No items in the cart
          </Typography>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleCheckout}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              handleBlur,
              setFieldValue,
              isSubmitting,
            }) => (
              <Form onSubmit={handleSubmit}>
                {/* Enter Email */}
                <Accordion
                  expanded={adjustedStep === 0}
                  onChange={() => setStep(0)}
                  className={`${classes.accordion} ${classes.accordionBoxShadow}`}
                  style={{
                    backgroundColor: "#000",
                    color: "#fff",
                    border: "1px solid #fff",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon style={{ color: "#fff" }} />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    className={classes.accordionSummary}
                  >
                    <Box className={classes.stepNumber}>1</Box>
                    <Typography variant="h6" className={classes.accordionTitle}>
                      ENTER EMAIL
                    </Typography>
                    {adjustedStep === 0 && (
                      <CheckCircleIcon className={classes.successIcon} />
                    )}
                  </AccordionSummary>
                  <AccordionDetails style={{ display: "block" }}>
                    <TextField
                      id="email"
                      name="email"
                      label="Email"
                      variant="outlined"
                      value={values?.email}
                      onChange={handleChange}
                      error={errors?.email && touched?.email}
                      onBlur={handleBlur}
                      fullWidth
                      style={{
                        marginTop: "16px",
                      }}
                      className={`${classes.darkTextField}`}
                    />
                    <Button
                      onClick={() => setStep(1)}
                      type="button"
                      variant="outlined"
                      style={{
                        marginTop: "16px",
                        marginBottom: "16px",
                        color: "#FECE00",
                        borderColor: "#FECE00",
                      }}
                      className={classes.btn}
                    >
                      Continue
                    </Button>
                  </AccordionDetails>
                </Accordion>

                {/* Enter Shipping Address */}
                {!isOnlyEbook && (
                  <Accordion
                    expanded={adjustedStep === 1}
                    onChange={() => setStep(1)}
                    className={`${classes.accordion} ${classes.accordionBoxShadow}`}
                    style={{
                      backgroundColor: "#000",
                      color: "#fff",
                      border: "1px solid #fff",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon style={{ color: "#fff" }} />}
                      aria-controls="panel2-content"
                      id="panel2-header"
                      className={classes.accordionSummary}
                    >
                      <Box className={classes.stepNumber}>2</Box>
                      <Typography
                        variant="h6"
                        className={classes.accordionTitle}
                      >
                        SHIPPING
                      </Typography>
                      {adjustedStep === 1 && (
                        <CheckCircleIcon className={classes.successIcon} />
                      )}
                    </AccordionSummary>
                    <AccordionDetails style={{ display: "block" }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Field
                            as={TextField}
                            name="name"
                            label="Name"
                            variant="outlined"
                            fullWidth
                            className={`${classes.darkTextField}`}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Field
                            as={TextField}
                            name="contact_email"
                            label="Contact Email"
                            variant="outlined"
                            fullWidth
                            className={`${classes.darkTextField}`}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            name="phone_number"
                            label="Mobile Number"
                            variant="outlined"
                            fullWidth
                            className={`${classes.darkTextField}`}
                            value={values.phone_number}
                            inputProps={{ maxLength: 15 }}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <Autocomplete
                            disableClearable
                            disablePortal
                            options={getCountries()}
                            onChange={(event, newValue) => {
                              setFieldValue("country", newValue?.value);
                              setFieldValue(
                                "country_code",
                                newValue?.countryCode
                              );
                              setFieldValue("state_name", "");
                              setFieldValue("state", "");
                              setFieldValue("city", "");
                            }}
                            value={values.country}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Country"
                                fullWidth
                                className={`${classes.darkTextField}`}
                              />
                            )}
                            style={{ height: "100%" }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Field
                            as={TextField}
                            name="country_code"
                            label="Code"
                            variant="outlined"
                            fullWidth
                            className={`${classes.darkTextField}`}
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <Autocomplete
                            disableClearable
                            disablePortal
                            options={getStates(values?.country_code)}
                            onChange={(event, newValue) => {
                              setFieldValue("state_name", newValue?.value);
                              setFieldValue("state", newValue?.stateCode);
                              setFieldValue("city", "");
                            }}
                            value={values?.state_name}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="State"
                                fullWidth
                                className={`${classes.darkTextField}`}
                              />
                            )}
                            style={{ height: "100%" }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Field
                            as={TextField}
                            name="state"
                            label="Code"
                            variant="outlined"
                            fullWidth
                            className={`${classes.darkTextField}`}
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Autocomplete
                            disableClearable
                            disablePortal
                            options={getCities(
                              values?.country_code,
                              values?.state
                            )}
                            onChange={(event, newValue) => {
                              setFieldValue("city", newValue?.value);
                            }}
                            value={values?.city}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="City"
                                fullWidth
                                className={`${classes.darkTextField}`}
                              />
                            )}
                            style={{ height: "100%" }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Field
                            as={TextField}
                            name="postcode"
                            label="Postcode"
                            variant="outlined"
                            fullWidth
                            className={`${classes.darkTextField}`}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Field
                            as={TextField}
                            name="street1"
                            label="Street 1"
                            variant="outlined"
                            fullWidth
                            className={`${classes.darkTextField}`}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            type="button"
                            variant="outlined"
                            style={{
                              marginTop: "16px",
                              marginBottom: "16px",
                              color: "#FECE00",
                              borderColor: "#FECE00",
                              float: "right",
                            }}
                            onClick={() => setStep(2)}
                          >
                            Continue
                          </Button>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Enter Payment Details */}
                <Accordion
                  expanded={adjustedStep === (isOnlyEbook ? 1 : 2)}
                  onChange={() => setStep(isOnlyEbook ? 1 : 2)}
                  className={`${classes.accordion} ${classes.accordionBoxShadow}`}
                  style={{
                    backgroundColor: "#000",
                    color: "#fff",
                    border: "1px solid #fff",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon style={{ color: "#fff" }} />}
                    aria-controls="panel3-content"
                    id="panel3-header"
                    className={classes.accordionSummary}
                  >
                    <Box className={classes.stepNumber}>
                      {isOnlyEbook ? 2 : 3}
                    </Box>
                    <Typography variant="h6" className={classes.accordionTitle}>
                      PAYMENT
                    </Typography>
                    {adjustedStep === (isOnlyEbook ? 1 : 2) && (
                      <CheckCircleIcon className={classes.successIcon} />
                    )}
                  </AccordionSummary>

                  <AccordionDetails style={{ display: "block" }}>
                    {/* Commenting this because its for future feature */}
                    {/* <RadioGroup
                      row
                      value={selectedPayment}
                      onChange={(e) => setSelectedPayment(e.target.value)}
                      style={{ marginBottom: "16px" }}
                    >
                      <FormControlLabel
                        value="bank_account"
                        control={<Radio style={{ color: "#fff" }} />}
                        label={
                          <Typography style={{ color: "#fff" }}>
                            Bank Account
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="e_money"
                        control={<Radio style={{ color: "#fff" }} />}
                        label={
                          <Typography style={{ color: "#fff" }}>
                            E-Money
                          </Typography>
                        }
                      />
                    </RadioGroup> */}

                    {/* Conditional Rendering Based on Selected Payment */}
                    {selectedPayment === "bank_account" ? (
                      <>
                        <CardElement
                          className={classes.cardElement}
                          options={{
                            style: {
                              base: { fontSize: "16px", color: "#fff" },
                              invalid: { color: "#9e2146" },
                            },
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Field
                          as={TextField}
                          name="emoneyAccount"
                          label="E-Money Account"
                          variant="outlined"
                          fullWidth
                          className={`${classes.darkTextField}`}
                        />
                      </>
                    )}

                    {/* Proceed to Payment Button */}
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div style={{ flexGrow: 1 }}>
                        {isSubmitting && (
                          <ClipLoader size={20} color="#ffffff" />
                        )}
                      </div>
                      <Button
                        type="submit"
                        variant="outlined"
                        style={{
                          marginTop: "16px",
                          marginBottom: "16px",
                          color: "#FECE00",
                          borderColor: "#FECE00",
                        }}
                        className={classes.btn}
                        disabled={isSubmitting}
                      >
                        Proceed to Payment
                      </Button>
                    </div>
                  </AccordionDetails>
                </Accordion>
              </Form>
            )}
          </Formik>
        )}
      </Paper>
    </>
  );
};

export default PaymentForm;
